import React from 'react'
import {Button, ButtonToolbar, Form, List, Notification, Stack, toaster} from 'rsuite'
import {GraphQLFormattedError} from 'graphql'
import {gql, useMutation, useQuery} from '@apollo/client'
import {ApolloError} from '@apollo/client'
import {importFailureReason, reportImportFailure} from './reportImportFailure'
import {AutoImportToggle} from './autoImportToggle'

const FRAGMENT = gql`
  fragment FoundationCouncilImportInfo on AutoImportFoundationCouncilInfoResponseImports {
    id
    date
    validationErrors
    errors
    importType
  }
`

const QUERY_INFO = gql`
  ${FRAGMENT}
  query AutoImportFoundationCouncilInfo {
    extensions {
      autoImportFoundationCouncilInfo {
        importTime
        importInterval
        autoImportEnabled
        status
        imports {
          ...FoundationCouncilImportInfo
        }
      }
    }
  }
`

const MUTATION_TRIGGER = gql`
  ${FRAGMENT}
  mutation TriggerAutoUploadFoundationCouncilMembers {
    extensions {
      triggerAutoUploadFoundationCouncilMembers {
        ...FoundationCouncilImportInfo
      }
    }
  }
`

type ImportInfo = {
  id: string
  date: string
  validationErrors: string
  errors: string[]
  importType: string
}

type InfoQueryData = {
  extensions: {
    autoImportFoundationCouncilInfo: {
      importTime: string
      importInterval: string
      autoImportEnabled: boolean
      status: string
      imports: (ImportInfo | null)[]
    }
  }
}

type TriggerMutationData = {
  extensions: {
    triggerAutoUploadFoundationCouncilMembers: {
      errors: string[]
      validationErrors: string
    }
  }
}

export function CustomViewFoundationCouncilImporter() {
  const [triggerImport, {loading: triggerImportLoading}] =
    useMutation<TriggerMutationData>(MUTATION_TRIGGER)
  const {data, refetch} = useQuery<InfoQueryData>(QUERY_INFO)

  function handleError(error: unknown) {
    toaster.push(
      <Notification
        closable
        type="error"
        header={(error as ApolloError).toString()}
        duration={5000}
      />,
      {placement: 'topEnd'}
    )
  }

  function handleResult(
    errors: readonly GraphQLFormattedError[] | undefined,
    errors2?: readonly (string | null)[] | undefined
  ) {
    if (errors) {
      toaster.push(
        <Notification closable type="error" header={errors.toString()} duration={5000} />,
        {placement: 'topEnd'}
      )
    } else if (errors2 && errors2.length > 0) {
      toaster.push(
        <Notification closable type="error" header={errors2.join('\n')} duration={5000} />,
        {placement: 'topEnd'}
      )
    } else {
      toaster.push(
        <Notification closable type="success" header="Operation successful" duration={5000} />,
        {placement: 'topEnd'}
      )
    }
    refetch()
  }

  async function onSubmitTrigger() {
    try {
      const {errors, data} = await triggerImport()
      const result = data?.extensions.triggerAutoUploadFoundationCouncilMembers
      const reason = importFailureReason({errors, result})
      if (reason) reportImportFailure('foundationCouncil', 'manual', reason)
      handleResult(errors, result?.errors)
    } catch (error) {
      reportImportFailure('foundationCouncil', 'manual', `${error}`)
      handleError(error)
    }
  }

  const info = data?.extensions.autoImportFoundationCouncilInfo

  return (
    <div>
      <h2>Foundation Council Auto Importer</h2>
      <h4>Source</h4>
      <a
        href="https://stdatahubexportprod.blob.core.windows.net/exports/FoundationCouncilVestedInterest/foundationcouncil_vestedinterest.csv"
        target="_blank"
        rel="noreferrer">
        stdatahubexportprod.blob.core.windows.net/.../foundationcouncil_vestedinterest.csv
      </a>
      <br />
      <br />
      <h4>Auto Import</h4>
      <AutoImportToggle importer="foundationCouncil" enabled={info?.autoImportEnabled} />
      <br />
      <br />
      <h4>Import Time</h4>
      {info?.importTime}
      <br />
      <br />
      <h4>Interval</h4>
      {info?.importInterval}
      <br />
      <br />
      <h4>Action</h4>
      <Form>
        <Form.Group>
          <ButtonToolbar>
            <Button
              disabled={triggerImportLoading}
              onClick={() => {
                onSubmitTrigger()
              }}>
              Trigger Import
            </Button>
          </ButtonToolbar>
        </Form.Group>
      </Form>
      <h4>History</h4>
      <List style={{maxHeight: '400px'}}>
        <List.Item>
          <Stack>
            <Stack.Item style={{width: '220px'}}>
              <b>Import Date:</b>
            </Stack.Item>
            <Stack.Item style={{width: '220px'}}>
              <b>Import Type:</b>
            </Stack.Item>
            <Stack.Item style={{width: '220px'}}>
              <b>Import Status:</b>
            </Stack.Item>
          </Stack>
        </List.Item>
        {info?.imports
          .filter((i): i is ImportInfo => i !== null)
          .map((importInfo) => (
            <List.Item key={importInfo.id}>
              <Stack>
                <Stack.Item style={{width: '220px'}}>
                  <a href={`/content/foundationCouncilImport/edit/${importInfo.id}/0`}>
                    {`${new Date(importInfo.date).toLocaleDateString('de-CH')} ${new Date(
                      importInfo.date
                    ).toLocaleTimeString('de-CH')}`}
                  </a>
                </Stack.Item>
                <Stack.Item style={{width: '220px'}}>{importInfo.importType}</Stack.Item>
                <Stack.Item style={{width: '220px'}}>
                  {importInfo.errors.length === 0 && importInfo.validationErrors.length === 0
                    ? 'Success'
                    : 'Failed'}
                </Stack.Item>
              </Stack>
            </List.Item>
          ))}
      </List>
    </div>
  )
}
